import { Container, Ticker } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { TakeWinAnimation } from '../../effects/TakeWinAnimation';
import type { PlayerProfile } from '../../model/profile/PlayerProfile';
import { safeAreaInsetTop } from '../../scenes/components/SlotHudComponent';
import type { ThemeAssetEntry } from '../../theme';
import type { BoardDialog } from '../board/BoardDialog';
import { DailyBonusBoard, type DailyBonusOffer } from '../slot/DailyBonusBoard';
import { SettingsBoard, type SlotSettingsHost } from '../slot/SettingsBoard';
import { WalletBoard } from '../slot/WalletBoard';
import { OverlayStage } from './OverlayStage';

/** The boards' art by URL (the app's bundled files). */
export interface OverlayBoardArt {
  /** The board's floor (the first slot's reel column). */
  floor: string;
  coin: string;
  gem: string;
  gift: string;
  check: string;
  /** The brass bezel the daily bonus's days sit in. */
  bezel: string;
  /** The painted button plates. */
  plates: Record<string, ThemeAssetEntry>;
}

/** The board on the overlay, as plain data, for browser tests. */
export type OverlayBoardUi =
  | { kind: 'settings'; ui: SettingsBoard['snapshot'] }
  | { kind: 'wallet'; ui: WalletBoard['snapshot'] }
  | { kind: 'daily'; ui: DailyBonusBoard['snapshot'] };

declare global {
  interface Window {
    /** The board open on the overlay canvas (off the slot), for browser tests. */
    __overlayBoard?: {
      ui: () => OverlayBoardUi;
      /** Where a button or line is, by its words (screen pixels). */
      where: (label: string) => { x: number; y: number } | null;
      /** The daily bonus: press its button by label. */
      press?: (label: string) => boolean;
      /** Settings: flip a switch by name, as a tap does. */
      toggle?: (name: 'music' | 'sfx' | 'gamble' | 'fen' | 'chatter') => Promise<void>;
    };
  }
}

const ALIAS = {
  floor: 'shared:board:floor',
  coin: 'shared:icon:coin',
  gem: 'shared:icon:gem',
  gift: 'shared:icon:gift',
  check: 'shared:icon:check',
  bezel: 'road:light_bezel',
} as const;

const texture = (name: keyof typeof ALIAS) => AssetLoader.shared.getTexture(ALIAS[name]);

async function loadArt(art: OverlayBoardArt): Promise<void> {
  const loader = AssetLoader.shared;
  await Promise.all([
    loader
      .loadTextures((Object.keys(ALIAS) as (keyof typeof ALIAS)[]).map((name) => ({ alias: ALIAS[name], src: art[name] })))
      .catch(() => undefined),
    loader.loadAssets(art.plates).catch(() => undefined),
  ]);
}

/** Where a board's button or row is on screen (browser tests). */
function whereIn(board: BoardDialog, label: string): { x: number; y: number } | null {
  let hit: Container | null = null;
  const walk = (node: Container): void => {
    if (hit || !node.visible) return;
    const text = (node as unknown as { text?: unknown }).text;
    if (typeof text === 'string' && text === label) hit = node;
    for (const child of node.children) walk(child as Container);
  };
  walk(board);
  if (!hit) return null;
  const b = (hit as Container).getBounds();
  return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
}

/**
 * Open one of the game's boards on the overlay canvas, over a screen that
 * isn't the slot (the slot draws its own on its canvas), until `play`
 * resolves. It takes every tap and key while it's up.
 */
async function onOverlay<T extends BoardDialog>(
  board: T,
  hook: (board: T) => NonNullable<Window['__overlayBoard']>,
  play: (board: T) => Promise<void>,
  extra: Container | null = null,
): Promise<void> {
  const handle = OverlayStage.shared.show(board, { modal: true, layer: 10 });
  const top = 64 + safeAreaInsetTop();
  board.setTopInset(top);
  const flight = extra ? OverlayStage.shared.show(Object.assign(extra, { layout: () => undefined }), { layer: 15 }) : null;
  try {
    if (await handle.shown) {
      if (flight) await flight.shown;
      window.__overlayBoard = hook(board);
      await play(board);
    }
  } finally {
    delete window.__overlayBoard;
    handle.remove();
    flight?.remove();
    board.destroy({ children: true });
    extra?.destroy({ children: true });
  }
}

let busy: Promise<void> = Promise.resolve();
/** One board at a time: a second waits for the first to close. */
function queued(run: () => Promise<void>): Promise<void> {
  busy = busy.then(run, run);
  return busy;
}

/**
 * The game's settings on a board over a screen that isn't the slot: the
 * same board the slot opens (music, sound effects, Gamble, the story,
 * notifications, restoring purchases, the account, and, at a wheel table,
 * the reel skin). Resolves when it closes.
 */
export function openSettingsOverlay(profile: PlayerProfile, host: SlotSettingsHost, art: OverlayBoardArt): Promise<void> {
  return queued(async () => {
    await loadArt(art);
    const board = new SettingsBoard(profile, host, () => texture('floor'));
    await onOverlay(
      board,
      (b) => ({ ui: () => ({ kind: 'settings', ui: b.snapshot }), where: (label) => whereIn(b, label), toggle: (name) => b.toggle(name) }),
      (b) => b.play(),
    );
  });
}

/** What gems and coins are for, on a board over a screen that isn't the
 *  slot. `reelGems`: gems land on this player's reels (reel_gems), which
 *  the board then says, as the slot's own board does. Resolves when it
 *  closes. */
export function openWalletOverlay(gems: number, coins: number, art: OverlayBoardArt, reelGems = false): Promise<void> {
  return queued(async () => {
    await loadArt(art);
    const board = new WalletBoard({ floor: () => texture('floor'), coin: () => texture('coin'), gem: () => texture('gem') });
    await onOverlay(
      board,
      (b) => ({ ui: () => ({ kind: 'wallet', ui: b.snapshot }), where: (label) => whereIn(b, label) }),
      (b) => b.play(gems, coins, reelGems),
    );
  });
}

/**
 * The daily bonus on a board over a screen that isn't the slot; the coins
 * claimed fly into the coin counter (`target`, screen pixels). Resolves
 * when it closes.
 */
export function playDailyBonusOverlay(
  offer: Omit<DailyBonusOffer, 'fly'>,
  art: OverlayBoardArt,
  target: () => { x: number; y: number } | null,
): Promise<void> {
  return queued(async () => {
    await loadArt(art);
    const board = new DailyBonusBoard({
      floor: () => texture('floor'),
      bezel: () => texture('bezel'),
      coin: () => texture('coin'),
      gift: () => texture('gift'),
      check: () => texture('check'),
    });
    const coins = new TakeWinAnimation(Ticker.shared);
    const layer = new Container();
    layer.addChild(coins);
    await onOverlay(
      board,
      (b) => ({ ui: () => ({ kind: 'daily', ui: b.snapshot }), press: (label) => b.press(label), where: (label) => whereIn(b, label) }),
      (b) =>
        b.play({
          ...offer,
          fly: async (from, amount) => {
            const to = target();
            if (!to || amount <= 0) return;
            await coins.takeWin(amount, from, to, () => undefined, {
              totalDurationMs: 1500,
              flyDurationMs: 700,
              maxCoins: Math.min(30, 10 + Math.round(Math.log2(1 + amount))),
            });
          },
        }),
      layer,
    );
  });
}
